import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Dropdown from '../../components/dropdown';
import { WeekdayHeader, MonthlyCard, DailyDetailCard } from './components';
import { logAPI, membershipAPI } from '../../actions/imsServer';
import { formatDateTime, isWebView } from '../../utils/base';
import { EXERCISE_TARGETS, API_PAGE_SIZE } from '../../constants';

export const ReportDidimMonthly = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const scrollContainerRef = useRef(null);
    const currentMonthRef = useRef(null);
    const hasInitialScrolledToCurrentMonth = useRef(false);

    // 사용자 정보 가져오기 (최상단에 선언)
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);

    // API 데이터 상태
    const [playLogs, setPlayLogs] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 웹뷰로 월간 didim, mins 전송
    useEffect(() => {
        if (isWebView() && playLogs.length > 0 && meInfo?.mainOrganizationId) {
            const mainOrgId = meInfo.mainOrganizationId;
            const orgLogs = playLogs.filter(log => log.organizationId === mainOrgId);
            const didim = orgLogs.reduce((acc, log) => acc + (log.stepCount || 0), 0);
            const durationSec = orgLogs.reduce((acc, log) => {
                let secs = 0;
                if (log.startTime && log.endTime) {
                    const start = new Date(log.startTime);
                    const end = new Date(log.endTime);
                    secs = Math.max(0, (end - start) / 1000);
                } else if (log.duration) {
                    secs = log.duration;
                }
                return acc + secs;
            }, 0);
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: 'playData',
                    data: {
                        didim: didim,
                        mins: (durationSec / 60).toFixed(2),
                        updateTime: new Date().getTime()
                    }
                })
            );
        }
    }, [playLogs, meInfo?.mainOrganizationId]);

    // Dropdown 상태 관리
    const [selectedOrganization, setSelectedOrganization] = useState(
        meInfo.mainOrganizationId?.toString() || ''
    );

    // 기관 목록 가져오기
    const fetchOrganizations = async () => {
        if (!meInfo.id) return;

        try {
            const response = await membershipAPI.getMemberships({
                userId: meInfo.id,
                type: 1,
                state: 0,
                expand: 'organization'
            });

            if (response?.data?.memberships) {
                const mainOrgId = meInfo.mainOrganizationId?.toString() || '';

                const orgList = response.data.memberships.map(m => ({
                    value: m.organizationId?.toString() || '',
                    label: m.organization?.name || t('no_organization')
                }));

                // main 기관을 맨 위로 정렬
                const sortedOrgList = orgList.sort((a, b) => {
                    const aIsMain = a.value === mainOrgId;
                    const bIsMain = b.value === mainOrgId;
                    if (aIsMain && !bIsMain) return -1;
                    if (!aIsMain && bIsMain) return 1;
                    return 0;
                });

                setOrganizations(sortedOrgList);

                // main 기관이 있으면 기본 선택
                if (!selectedOrganization && mainOrgId) {
                    const mainOrgExists = sortedOrgList.some(org => org.value === mainOrgId);
                    if (mainOrgExists) {
                        setSelectedOrganization(mainOrgId);
                    }
                }
            }
        } catch (err) {
            console.error('기관 목록 로딩 실패:', err);
        }
    };

    // 월간 플레이 로그 데이터 가져오기 (전체 데이터)
    const fetchMonthlyPlayLogs = useCallback(async () => {
        if (!meInfo.id) return;

        try {
            setLoading(true);
            setError(null);

            // 전체 기간 데이터를 100건씩 배치 조회
            const fetchPlayLogsBatched = async () => {
                let all = [];
                let offset = 0;
                let page;
                do {
                    const res = await logAPI.getPlayLogs({
                        userId: meInfo.id,
                        organizationId: selectedOrganization || undefined,
                        limit: API_PAGE_SIZE,
                        expand: 'organization',
                        offset
                    });
                    page = res?.data?.logs || [];
                    all = all.concat(page);
                    offset += API_PAGE_SIZE;
                } while (page.length === API_PAGE_SIZE);
                return all;
            };
            const allLogs = await fetchPlayLogsBatched();

            setPlayLogs(allLogs);
        } catch (err) {
            console.error('월간 플레이 로그 데이터 로딩 실패:', err);
            setError(err.message);
            setPlayLogs([]);
        } finally {
            setLoading(false);
        }
    }, [meInfo.id, selectedOrganization]);

    // 컴포넌트 마운트 시 데이터 로드 (fetch 함수는 의존성에서 제외 → 반복 호출 방지)
    useEffect(() => {
        if (!meInfo.id) return;

        const loadData = async () => {
            await Promise.all([
                fetchOrganizations(),
                fetchMonthlyPlayLogs()
            ]);
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrganizations는 일반 함수, fetchMonthlyPlayLogs는 useCallback이지만 의존성 제외로 1회만 실행
    }, [meInfo.id]);

    // 선택된 기관 변경 시 데이터 다시 로드
    useEffect(() => {
        if (!meInfo.id) return;
        fetchMonthlyPlayLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedOrganization 변경 시에만 재실행
    }, [meInfo.id, selectedOrganization]);

    // 초기 진입 시 이번 달 카드가 보이도록 스크롤
    useEffect(() => {
        if (loading || error || hasInitialScrolledToCurrentMonth.current) return;
        if (scrollContainerRef.current && currentMonthRef.current) {
            const container = scrollContainerRef.current;
            const target = currentMonthRef.current;
            const containerTop = container.getBoundingClientRect().top;
            const targetTop = target.getBoundingClientRect().top;
            const currentScrollTop = container.scrollTop;
            const scrollOffset = targetTop - containerTop + currentScrollTop;

            container.scrollTo({
                top: Math.max(0, scrollOffset - 20),
                behavior: 'auto'
            });
            hasInitialScrolledToCurrentMonth.current = true;
        }
    }, [loading, error]);

    // 날짜 선택 시 해당 월 달력으로 스크롤
    const selectedMonthRef = useRef(null);

    useEffect(() => {
        if (selectedDate && selectedMonth && selectedYear && selectedMonthRef.current && scrollContainerRef.current) {
            setTimeout(() => {
                const container = scrollContainerRef.current;
                const targetElement = selectedMonthRef.current;

                if (container && targetElement) {
                    const containerTop = container.getBoundingClientRect().top;
                    const targetTop = targetElement.getBoundingClientRect().top;
                    const currentScrollTop = container.scrollTop;
                    const scrollOffset = targetTop - containerTop + currentScrollTop;

                    container.scrollTo({
                        top: Math.max(0, scrollOffset - 20),
                        behavior: 'smooth'
                    });
                }
            }, 100);
        }
    }, [selectedDate, selectedMonth, selectedYear]);

    // 선택한 날짜의 데이터 생성 함수
    const getDailyData = (date, month, year) => {

        const targetDate = new Date(year, month - 1, date);
        const targetDateStr = formatDateTime(targetDate, '-', ':', 0, 10);

        const dayLogs = playLogs.filter(log => {
            const logDate = new Date(log.time);
            const logDateStr = formatDateTime(logDate, '-', ':', 0, 10);
            return logDateStr === targetDateStr;
        });

        if (dayLogs.length > 0) {
            // 일별로 합산
            const stepCount = dayLogs.reduce((sum, log) => sum + (log.stepCount || 0), 0);

            let calories = 0;
            dayLogs.forEach(log => {
                // endCalories - startCalories 계산
                if (log.endCalories !== undefined && log.startCalories !== undefined) {
                    calories += (log.endCalories - log.startCalories);
                } else {
                    calories += log.calories || 0;
                }
            });

            let time = 0;
            dayLogs.forEach(log => {
                // endTime - startTime 계산
                if (log.startTime && log.endTime) {
                    const start = new Date(log.startTime);
                    const end = new Date(log.endTime);
                    time += Math.floor((end - start) / 1000);
                } else {
                    time += log.duration || 0;
                }
            });

            return {
                stepCount,
                calories,
                time // 분 단위로 변환
            };
        }

        return {
            stepCount: 0,
            calories: 0,
            time: 0,
        };
    };

    const handleOrganizationChange = (item) => {
        setSelectedOrganization(item.value);
    };

    // Monthly exercise data - 실제 데이터 기반 (중간 빈 달 포함)
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    const monthlyData = useMemo(() => {
        // 월별 + 일별 그룹화하여 합산
        const logsByMonth = {};
        playLogs.forEach(log => {
            const logDate = new Date(log.time);
            if (Number.isNaN(logDate.getTime())) return;

            const year = logDate.getFullYear();
            const month = logDate.getMonth() + 1;
            const monthKey = `${year}-${String(month).padStart(2, '0')}`;
            const dateStr = formatDateTime(logDate, '-', ':', 0, 10);

            if (!logsByMonth[monthKey]) {
                logsByMonth[monthKey] = {
                    year,
                    month,
                    logsByDate: {},
                };
            }

            if (!logsByMonth[monthKey].logsByDate[dateStr]) {
                logsByMonth[monthKey].logsByDate[dateStr] = {
                    date: dateStr,
                    totalSteps: 0,
                    totalTime: 0,
                    calories: 0
                };
            }

            const dayLog = logsByMonth[monthKey].logsByDate[dateStr];
            dayLog.totalSteps += log.stepCount || 0;

            if (log.startTime && log.endTime) {
                const start = new Date(log.startTime);
                const end = new Date(log.endTime);
                dayLog.totalTime += Math.floor((end - start) / 1000);
            } else {
                dayLog.totalTime += log.duration || 0;
            }

            if (log.endCalories !== undefined && log.startCalories !== undefined) {
                dayLog.calories += (log.endCalories - log.startCalories);
            } else {
                dayLog.calories += log.calories || 0;
            }
        });

        // 월간 통계 계산 함수 (이미 일별로 합산된 데이터를 받음)
        const calculateMonthlyStats = (logs, month, year) => {
            const stepCount = logs.reduce((sum, log) => sum + (log.totalSteps || 0), 0);
            const calories = logs.reduce((sum, log) => sum + (log.calories || 0), 0);
            const time = logs.reduce((sum, log) => sum + (log.totalTime || 0), 0);

            // 목표달성 기준 계산 (이미 일별로 합산된 데이터이므로 각 log가 하루치 합산 데이터)
            const achievedDays = logs.filter(log => {
                const dayTotalSteps = log.totalSteps || 0;
                const dayCalories = log.calories || 0;
                const dayTotalTime = log.totalTime || 0;

                return dayTotalSteps >= EXERCISE_TARGETS.STEPS &&
                    dayCalories >= EXERCISE_TARGETS.CALORIES &&
                    Math.floor(dayTotalTime / 60) >= EXERCISE_TARGETS.ACTIVITY_TIME;
            }).length;

            // 목표달성일 계산
            const currentDate = new Date();
            const isCurrentMonth = month === currentDate.getMonth() + 1 && year === currentDate.getFullYear();
            const lastDayOfMonth = new Date(year, month, 0).getDate();
            const today = currentDate.getDate();

            const goalDays = isCurrentMonth ? Math.min(lastDayOfMonth, today) : lastDayOfMonth;

            return {
                stepCount,
                calories,
                time: Math.floor(time / 60),
                achievedDays,
                goalDays
            };
        };

        const sortedMonths = Object.values(logsByMonth).sort(
            (a, b) => (a.year - b.year) || (a.month - b.month)
        );

        // 데이터가 없는 경우에는 이번 달만 표시
        if (sortedMonths.length === 0) {
            const stats = calculateMonthlyStats([], currentMonth, currentYear);
            return [{
                month: currentMonth,
                year: currentYear,
                goalDays: stats.goalDays,
                achievedDays: stats.achievedDays,
                stepCount: stats.stepCount,
                calories: stats.calories,
                time: stats.time,
                dailyData: [],
                isCurrentMonth: true,
                isDidim: true,
            }];
        }

        const first = sortedMonths[0];
        const range = [];
        let cursorYear = first.year;
        let cursorMonth = first.month;

        while (cursorYear < currentYear || (cursorYear === currentYear && cursorMonth <= currentMonth)) {
            range.push({ year: cursorYear, month: cursorMonth });
            cursorMonth += 1;
            if (cursorMonth > 12) {
                cursorMonth = 1;
                cursorYear += 1;
            }
        }

        return range.map(({ year, month }) => {
            const key = `${year}-${String(month).padStart(2, '0')}`;
            const monthLogs = Object.values(logsByMonth[key]?.logsByDate || {});
            const stats = calculateMonthlyStats(monthLogs, month, year);
            const isCurrentMonth = month === currentMonth && year === currentYear;

            return {
                month,
                year,
                goalDays: stats.goalDays,
                achievedDays: stats.achievedDays,
                stepCount: stats.stepCount,
                calories: stats.calories,
                time: stats.time,
                dailyData: monthLogs,
                isCurrentMonth,
                isDidim: true,
            };
        });
    }, [playLogs, currentMonth, currentYear]);

    return (
        <ContentArea sx={{
            height: '100vh',
            overflow: 'hidden',
            paddingBottom: 0,
            width: '100%', px: '10px'
        }}>
            {/* Organization Dropdown */}
            <Dropdown
                options={organizations}
                value={selectedOrganization}
                onChange={handleOrganizationChange}
                placeholder={t('select_organization')}
                sx={{
                    marginRight: '10px',
                    alignSelf: 'flex-end'
                }}
            />

            {/* Loading State */}
            {loading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1">{t('loading_data')}</Typography>
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="error">
                        {t('an_error_occurred_while_loading_data_message', { message: error })}
                    </Typography>
                </Box>
            )}

            {/* Content */}
            {!loading && !error && (
                <>
                    {/* Weekday Header - Fixed */}
                    <WeekdayHeader />

                    <Box
                        ref={scrollContainerRef}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px',
                            width: '100%',
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            paddingBottom: '100px',
                            backgroundColor: theme.palette.figma.mainBg,
                        }}
                    >
                        {monthlyData.map((month) => (
                            <React.Fragment key={`${month.year}-${month.month}`}>
                                <Box
                                    ref={(el) => {
                                        if (month.isCurrentMonth) {
                                            currentMonthRef.current = el;
                                        }
                                        if (selectedDate && selectedMonth === month.month && selectedYear === month.year) {
                                            selectedMonthRef.current = el;
                                        }
                                    }}
                                >
                                    <MonthlyCard
                                        month={month.month}
                                        year={month.year}
                                        goalDays={month.goalDays}
                                        achievedDays={month.achievedDays}
                                        stepCount={month.stepCount}
                                        calories={month.calories}
                                        time={month.time}
                                        dailyData={month.dailyData}
                                        isCurrentMonth={month.isCurrentMonth}
                                        isDidim={month.isDidim}
                                        selectedDate={selectedDate && selectedMonth === month.month && selectedYear === month.year ? selectedDate : null}
                                        monthlyData={month}
                                        onDateClick={(day, m, y) => {
                                            setSelectedDate(day);
                                            setSelectedMonth(m);
                                            setSelectedYear(y);
                                        }}
                                    />
                                </Box>

                                {/* Daily Detail Card - 선택된 날짜가 해당 월에 속하면 해당 월 달력 바로 아래에 표시 */}
                                {selectedDate && selectedMonth === month.month && selectedYear === month.year && (() => {
                                    const dailyData = getDailyData(selectedDate, selectedMonth, selectedYear);

                                    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
                                    const selectedDateObj = new Date(selectedYear, selectedMonth - 1, selectedDate);

                                    const firstMonth = monthlyData[0];
                                    const startDate = firstMonth
                                        ? new Date(firstMonth.year, firstMonth.month - 1, 1)
                                        : new Date(currentYear, currentMonth - 1, 1);
                                    const endDate = new Date(currentYear, currentMonth - 1, currentDate.getDate());

                                    const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
                                    const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
                                    const prevMonthDays = new Date(prevYear, prevMonth, 0).getDate();
                                    const nextMonth = selectedMonth === 12 ? 1 : selectedMonth + 1;
                                    const nextYear = selectedMonth === 12 ? selectedYear + 1 : selectedYear;

                                    const canGoPrevious = selectedDateObj > startDate;
                                    const canGoNext = selectedDateObj < endDate;

                                    return (
                                        <DailyDetailCard
                                            selectedDate={typeof selectedDate === 'number' ? selectedDate : (selectedDate ? selectedDate.getDate() : 1)}
                                            selectedMonth={selectedMonth}
                                            selectedYear={selectedYear}
                                            totalSteps={dailyData.stepCount}
                                            calories={dailyData.calories}
                                            totalTime={dailyData.time}
                                            isDidim={true}
                                            showPrevious={canGoPrevious}
                                            showNext={canGoNext}
                                            onClose={() => {
                                                setSelectedDate(null);
                                                setSelectedMonth(null);
                                                setSelectedYear(null);
                                            }}
                                            onPrevious={() => {
                                                if (selectedDate > 1) {
                                                    setSelectedDate(selectedDate - 1);
                                                } else {
                                                    setSelectedDate(prevMonthDays);
                                                    setSelectedMonth(prevMonth);
                                                    setSelectedYear(prevYear);
                                                }
                                            }}
                                            onNext={() => {
                                                if (selectedDate < daysInMonth) {
                                                    setSelectedDate(selectedDate + 1);
                                                } else {
                                                    setSelectedDate(1);
                                                    setSelectedMonth(nextMonth);
                                                    setSelectedYear(nextYear);
                                                }
                                            }}
                                        />
                                    );
                                })()}
                            </React.Fragment>
                        ))}

                    </Box>
                </>
            )}
        </ContentArea >
    );
};

export default ReportDidimMonthly;